import { useMemo, useRef, useCallback } from 'react';
import { MapType } from '../CommonTypes';
import { useLocations } from '../hooks/useLocations';

import ZoomableImage from './ZoomableImage';
import { CustomShapeArea } from './CustomShapeArea';
import { useAtom } from 'jotai';
import { hoveredMapAreaAtom } from '../states/planner';
import { AreaMapHover } from './AreaMapHover';
import useMaps from '../hooks/useMaps';

export const MapView = ({ map, setCurrentMap }: { map: MapType; setCurrentMap: (map: MapType) => void }) => {
  const [, setHoveredMapArea] = useAtom(hoveredMapAreaAtom);
  const [maps] = useMaps();

  const [viewBox, locations] = useLocations(map.svgurl);

  // ---- Preload all other map images AFTER the first visible one loads ----
  const allUrls = useMemo(() => {
    const urls = maps.map(m => m.imageurl).filter(Boolean);
    // ensure uniqueness
    return Array.from(new Set(urls));
  }, [maps]);

  const currentUrl = map?.imageurl;
  const restUrls = useMemo(() => allUrls.filter(u => u !== currentUrl), [allUrls, currentUrl]);

  // Avoid duplicate preloads for the same map set
  const preloadedKeyRef = useRef<string | null>(null);
  const mapsKey = useMemo(() => allUrls.join('|'), [allUrls]);

  const preloadImages = useCallback((urls: string[]) => {
    urls.forEach(url => {
      const img = new Image();
      // hint for async decoding where supported
      img.decoding = 'async';
      img.src = url;
    });
  }, []);

  const handleFirstImageLoad = useCallback(() => {
    if (!mapsKey) return;
    if (preloadedKeyRef.current === mapsKey) return;

    preloadedKeyRef.current = mapsKey;
    if (restUrls.length) preloadImages(restUrls);
  }, [mapsKey, restUrls, preloadImages]);

  // -----------------------------------------------------------------------

  return (
    <>
      {/* Ensure ZoomableImage forwards onLoad to its underlying <img> */}
      <ZoomableImage src={map.imageurl} alt="MapImage" onLoad={handleFirstImageLoad} />
      <AreaMapHover />
      {locations.length > 0 && (
        <svg className="absolute left-0 top-0" viewBox={`${viewBox}`} preserveAspectRatio="none">
          {locations.map(l => {
            if (l.type === 'view') {
              return (
                <CustomShapeArea
                  key={l.id}
                  location={l}
                  className="view-path"
                  setArea={setHoveredMapArea}
                  onClick={e => {
                    e.preventDefault();
                    setHoveredMapArea(null);
                    setCurrentMap(maps[Number(l.ref_id)]);
                  }}
                />
              );
            }
            return null;
          })}
        </svg>
      )}
    </>
  );
};
